import '../avatar-upload.scss';

import { first } from 'lodash';
import {
  ChangeEvent,
  ComponentPropsWithRef,
  FocusEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  FileItemData,
  getFileDataFromFile,
  isValidFileSize,
  isValidFileType,
} from '../../../../data/utils/upload.utils';
import { Tooltip } from '../../../tooltip/tooltip';

export type AvatarUploadProps = {
  error?: string | { error: string; [key: string]: string };
  fileTypes: string[];
  fileSize: string;
  value?: FileItemData | null;
  onChange?: (value: FileItemData | null, e: ChangeEvent<HTMLElement>) => void;
  onBlur?: (value: FileItemData | null, e: FocusEvent<HTMLElement>) => void;
};

type InputUploadControllerProps = Omit<
  ComponentPropsWithRef<typeof AvatarUpload>,
  'name' | 'defaultValue' | 'onChange' | 'onBlur'
> & {
  name: string;
  defaultValue?: FileItemData | null;
  onChange?: (value: FileItemData | null, e: ChangeEvent<HTMLElement>) => void;
  onBlur?: (value: FileItemData | null, e: FocusEvent<HTMLElement>) => void;
};

export function AvatarUploadController(props: InputUploadControllerProps) {
  const { defaultValue, onChange, onBlur, ...defaultProps } = props;

  return (
    <Controller
      render={({
        field: {
          onChange: onControllerChange,
          onBlur: onControllerBlur,
          value,
        },
        fieldState: { error },
      }) => (
        <AvatarUpload
          {...defaultProps}
          onChange={(val, e) => {
            onControllerChange(val);

            onChange?.(val, e);
          }}
          onBlur={(val, e) => {
            onControllerChange(val);

            onControllerBlur();

            onBlur?.(val, e);
          }}
          value={value ?? ''}
          error={error?.message}
        />
      )}
      name={props.name}
      defaultValue={defaultValue ?? ''}
    />
  );
}

export function AvatarUpload({
  fileTypes = [],
  fileSize = '2MB',
  onChange,
  onBlur,
  ...props
}: AvatarUploadProps) {
  const intl = useIntl();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState<FileItemData | null>(null);
  const [error, setError] = useState<
    string | { error: string; [key: string]: string } | null
  >(null);

  useEffect(() => {
    setValue(props.value ?? null);
  }, [props.value]);

  useEffect(() => {
    setError(props.error ?? null);
  }, [props.error]);

  const getFile = useCallback(
    async (file: File | null | undefined) => {
      let data: FileItemData | null = null;
      let fileError: string | null = null;

      if (file) {
        if (!isValidFileType(file?.name, fileTypes)) {
          fileError = intl.formatMessage({
            id: 'common.file-upload.error.type',
          });
        } else if (!isValidFileSize(file?.size, fileSize)) {
          fileError = intl.formatMessage({
            id: 'common.file-upload.error.size',
          });
        } else {
          data = await getFileDataFromFile(file);
          fileError = null;
        }
      }

      setError(fileError);
      return { data, error: fileError };
    },
    [fileTypes, fileSize, intl]
  );

  const onHandleFiles = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const { data: fileData } = await getFile(
        first((e.currentTarget as HTMLInputElement).files)
      );

      setValue(fileData);

      onChange?.(fileData, e as unknown as ChangeEvent<HTMLElement>);

      onBlur?.(fileData, e as unknown as FocusEvent<HTMLElement>);
    },
    [getFile, onChange, onBlur]
  );

  const onHandleButton = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      setValue(null);
      setError(null);

      onChange?.(null, e as unknown as ChangeEvent<HTMLElement>);

      onBlur?.(null, e as unknown as FocusEvent<HTMLElement>);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [fileInputRef, onChange, onBlur]
  );

  const errorMessage = typeof error === 'string' ? error : error?.error ?? '';

  return (
    <Tooltip message={errorMessage} disabled={!error} error={!!error}>
      <div className={`avatar-upload${error ? ' invalid' : ''}`}>
        {value ? <img src={value.base64} alt={value.name} /> : <span />}

        <input
          type="file"
          accept={fileTypes.join(',')}
          onChange={onHandleFiles}
          ref={fileInputRef}
        />

        <button type="button" onClick={onHandleButton} />
      </div>
    </Tooltip>
  );
}
